import React from 'react';

import {
  Add as AddIcon,
  BorderColor as BorderColorIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteFilledIcon,
  Info as InfoIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router';

import { appRoutes, RouterEntitiesId, STATUSES_FILTER_OPTIONS } from '@clearverify/constants';
import { formatPhoneNumber, parseNullableValue } from '@clearverify/utils';

import {
  PermissiblePurposeStatus,
  VerifierCredentialStatus,
  type VerifierInfoFragment,
} from 'src/api';
import { Tooltip, UserStatusChip } from 'src/components/common';
import { DemoPrivacyText } from 'src/components/demoPrivacyMode';
import type { TableColumn, TableSelectedRowId } from 'src/components/table';
import {
  Table,
  TableExcelColumn,
  TableFilter,
  TableRowAction,
  TableTopAction,
} from 'src/components/table';
import { COMPANY_SEARCH_CATEGORIES } from 'src/constants/company';
import { ModalId } from 'src/constants/modals';
import {
  VERIFIER_CREDENTIAL_STATUS_LABELS,
  VERIFIER_TYPE_FILTER_OPTIONS,
} from 'src/constants/verifier';
import { useFilterStorage, useSearch } from 'src/hooks/common';
import { useGetMiddeskStatus } from 'src/hooks/middesk/useGetMiddeskStatus';
import { useModal, useModalV2 } from 'src/hooks/shared';
import { useCurrentUserPermissions } from 'src/hooks/user';
import { DeleteVerifierConfirmationModal } from 'src/modals/verifier/DeleteVerifierConfirmationModal';
import { PaginationContextValue } from 'src/providers/PaginationProvider';
import { formatFeinNumber } from 'src/utils/fein';
import { buildUrl } from 'src/utils/router';
import { getVerifierStatus } from 'src/utils/verifiers';

import { ClickableLabel } from '../common';

import { isManualVerificationDisabled, isMiddeskVerificationDisabled } from './utils';

type VerifiersTableClearVerifyStaffProps = {
  verifiersList: VerifierInfoFragment[];
  verifiersCount: number;
  onRefresh?: () => Promise<void>;
  pagination: PaginationContextValue;
  loading: boolean;
};

export const VerifiersTableClearVerifyStaff: React.FC<VerifiersTableClearVerifyStaffProps> = ({
  verifiersList,
  verifiersCount,
  onRefresh,
  pagination,
  loading,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { openModal } = useModal();
  const { openModal: openModalV2 } = useModalV2();
  const { setSearchValue, searchValue, setSearchCategory, searchCategory } = useSearch();
  const { setSortOption } = useFilterStorage();

  const { isPlatformAdmin } = useCurrentUserPermissions();

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect must not depend on "pagination" here
  React.useEffect(() => {
    pagination.resetPage();
  }, [searchValue, searchCategory]);

  const isMiddeskEnabled = useGetMiddeskStatus();

  const [selectedIds, setSelectedIds] = React.useState<TableSelectedRowId[]>([]);

  const rowActions = React.useMemo(
    (): Array<TableRowAction<VerifierInfoFragment>> => [
      {
        startIcon: <BorderColorIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'View Details',
        onAction: verifier => {
          navigate(buildDetailsUrl(verifier.id));
        },
      },
      ...(isMiddeskEnabled
        ? [
            {
              startIcon: <CheckCircleIcon style={{ margin: '0 10px 0 0', color: '#388E3C' }} />,
              actionName: 'Middesk Verification',
              disabled: (verifier: VerifierInfoFragment) => isMiddeskVerificationDisabled(verifier),
              onAction: (verifier: VerifierInfoFragment) =>
                openModal(ModalId.VerifierVerifyAutomaticByCVStaffModal, {
                  verifierId: verifier.id,
                }),
            },
          ]
        : []),
      {
        startIcon: <VerifiedIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: verifier =>
          isManualVerificationDisabled(verifier, isMiddeskEnabled)
            ? 'Manual Verification\n(No Active Admins yet)'
            : 'Manual Verification',
        disabled: verifier => isManualVerificationDisabled(verifier, isMiddeskEnabled),
        onAction: verifier => openModal(ModalId.VerifierVerifyManuallyByCVStaffModal, { verifier }),
      },
      ...(isPlatformAdmin
        ? [
            {
              startIcon: <DeleteFilledIcon sx={{ margin: '0 10px 0 0', color: 'error.main' }} />,
              actionName: 'Delete Company',
              style: { color: theme.palette.error.main },
              onAction: (verifier: VerifierInfoFragment) => {
                openModalV2(DeleteVerifierConfirmationModal, {
                  id: ModalId.DeleteVerifierConfirmationModal,
                  verifierId: verifier.id,
                });
              },
            },
          ]
        : []),
    ],
    [navigate, openModal, openModalV2, isPlatformAdmin, isMiddeskEnabled, theme.palette.error.main],
  );

  const topActions = React.useMemo(
    (): Array<TableTopAction> => [
      {
        actionName: 'Add',
        onAction: () => openModal(ModalId.InviteVerifiersAdminsByCVAdmin),
        icon: <AddIcon />,
        show: true,
        render() {
          return (
            <Button
              key={this.actionName}
              variant="contained"
              startIcon={this.icon}
              disabled={loading}
              loading={loading}
              onClick={this.onAction}
            >
              New
            </Button>
          );
        },
      },
    ],
    [loading, openModal],
  );

  return (
    <React.Fragment>
      <Table
        dataSource={verifiersList}
        rowCount={verifiersCount}
        columns={columns}
        rowActions={rowActions}
        topActions={topActions}
        onSearch={setSearchValue}
        onSort={setSortOption}
        pagination={pagination}
        filters={filters}
        searchCategories={COMPANY_SEARCH_CATEGORIES}
        onSearchCategorySelect={setSearchCategory}
        selectable
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        excelColumns={excelColumns}
        onRefresh={onRefresh}
        loading={loading}
      />
    </React.Fragment>
  );
};

const buildDetailsUrl = (verifierId: string) => {
  return buildUrl(appRoutes.verifiers.detail.companyInformation, {
    [RouterEntitiesId.verifier]: verifierId,
  });
};

const columns: Array<TableColumn<VerifierInfoFragment>> = [
  {
    title: 'Name',
    key: 'displayName',
    sortable: true,
    render: (_valueByDataPath, verifier) => {
      const isPending = verifier.permissiblePurposesList.some(
        item => item.status === PermissiblePurposeStatus.Pending,
      );

      return (
        <Box display="flex" gap="5px" alignItems="center">
          <ClickableLabel
            type="link"
            to={buildDetailsUrl(verifier.id)}
            label={verifier.displayName}
            logoUrl={verifier.logo?.downloadUrl}
          />
          {isPending && (
            <Tooltip
              title="Verifier requested a new Permissible purpose"
              sx={{ backgroundColor: 'white' }}
            >
              <InfoIcon
                color="warning"
                sx={{
                  width: '16px',
                  height: '16px',
                  fontSize: '16px',
                }}
              />
            </Tooltip>
          )}
        </Box>
      );
    },
  },
  {
    title: 'FEIN',
    key: 'fein',
    render: (_valueByDataPath, verifier) => {
      return (
        <Typography variant="body2">
          <DemoPrivacyText>{formatFeinNumber(verifier.fEIN)}</DemoPrivacyText>
        </Typography>
      );
    },
  },
  {
    title: 'Contact Email',
    key: 'contactEmail',
    sortable: true,
    render: (_valueByDataPath, verifier) => {
      return (
        <Typography variant="body2">
          <DemoPrivacyText>{verifier.contactEmail || '-'}</DemoPrivacyText>
        </Typography>
      );
    },
  },
  {
    title: 'Contact Phone',
    key: 'contactPhone',
    render: (_valueByDataPath, verifier) => {
      return (
        <Typography variant="body2">
          <DemoPrivacyText>{formatPhoneNumber(verifier.contactPhone) || '-'}</DemoPrivacyText>
        </Typography>
      );
    },
  },
  {
    title: 'Type',
    key: 'type',
    sortable: true,
    render: (_valueByDataPath, verifier) => {
      return <Typography variant="body2">{parseNullableValue(verifier.type)}</Typography>;
    },
  },
  {
    title: 'Status',
    key: 'status',
    render: (_valueByDataPath, verifier) => {
      return <UserStatusChip userStatus={getVerifierStatus(verifier)} />;
    },
  },
  {
    title: 'Credentialed',
    key: 'credentialStatus',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, verifier) => {
      return (
        <Box display="flex" gap="5px" alignItems="center">
          <Typography variant="body2">
            {VERIFIER_CREDENTIAL_STATUS_LABELS[verifier.credentialStatus]}
          </Typography>
          {verifier.credentialStatus === VerifierCredentialStatus.Denied && (
            <Tooltip title={verifier.denialReason}>
              <InfoIcon
                color="action"
                sx={{
                  width: '16px',
                  height: '16px',
                  fontSize: '16px',
                }}
              />
            </Tooltip>
          )}
        </Box>
      );
    },
  },
];

const filters: TableFilter[] = [
  {
    id: 'type',
    type: 'autocomplete',
    fieldProps: {
      label: 'Verifier type',
      options: VERIFIER_TYPE_FILTER_OPTIONS,
    },
  },
  {
    id: 'status',
    type: 'select',
    fieldProps: {
      label: 'Status',
      multiple: true,
      options: STATUSES_FILTER_OPTIONS,
    },
  },
];

const excelColumns: Array<TableExcelColumn<VerifierInfoFragment>> = [
  {
    header: 'Name',
    key: 'displayName',
  },
  {
    header: 'FEIN',
    key: 'fein',
    render: (_valueByKey, verifier) => {
      return formatFeinNumber(verifier.fEIN);
    },
  },
  {
    header: 'Contact Phone',
    key: 'contactPhone',
    render: (_valueByKey, verifier) => {
      return formatPhoneNumber(verifier.contactPhone);
    },
  },
  {
    header: 'Type',
    key: 'type',
  },
  {
    header: 'Status',
    key: 'status',
    render: (_valueByKey, verifier) => {
      return getVerifierStatus(verifier);
    },
  },
  {
    header: 'Credential Status',
    key: 'credentialStatus',
  },
];
